/**
 * Wheel of Fortune - Pixi.js Renderer (Premium Theme System)
 * Phase 7.0+: Full theme integration with gradients, gloss, bevel
 */

import * as PIXI from 'pixi.js';
import type { WheelSegment } from './types';
import type { WheelConfig } from '../runtime/types';
import { WHEEL_THEMES, type WheelTheme } from './themes/wheelThemes';

/**
 * Color utilities
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : { r: 0, g: 0, b: 0 };
}

function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  // Relative luminance formula
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

function getContrastColor(hex: string): string {
  return getLuminance(hex) > 0.5 ? '#000000' : '#FFFFFF';
}

function parseColor(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}

export class WheelRenderer extends PIXI.Container {
  private wheel: PIXI.Container;
  private shadowLayer: PIXI.Container;
  private config: WheelConfig;
  private theme: WheelTheme;
  private wheelRadius: number;

  constructor(config: WheelConfig, radius: number) {
    super();

    this.config = config;
    const themeKey = (config.theme as keyof typeof WHEEL_THEMES) || 'modern';
    this.theme = WHEEL_THEMES[themeKey] || WHEEL_THEMES.modern;
    this.wheelRadius = radius;

    // Create layers for depth
    this.shadowLayer = new PIXI.Container();
    this.wheel = new PIXI.Container();
    
    this.addChild(this.shadowLayer);
    this.addChild(this.wheel);

    this.render();
  }

  private render(): void {
    // Clear previous render
    this.shadowLayer.removeChildren();
    this.wheel.removeChildren();

    // Draw bottom-up for proper layering
    this.drawShadow();
    this.drawSegments();
    this.drawInnerRim();
    this.drawOuterRim();
    if (this.theme.wheel.gloss.enabled) {
      this.drawGloss();
    }
    this.drawCenterCircle();
  }

  /**
   * Draw soft shadow under wheel (from theme)
   */
  private drawShadow(): void {
    const { shadow } = this.theme.wheel.rim;
    const shadowGraphics = new PIXI.Graphics();
    
    // Elliptical shadow
    shadowGraphics.beginFill(parseColor(shadow.color), shadow.alpha);
    shadowGraphics.drawEllipse(0, shadow.y, this.wheelRadius * 1.1, this.wheelRadius * 0.15);
    shadowGraphics.endFill();
    
    // Blur filter for softness
    shadowGraphics.filters = [new PIXI.BlurFilter(shadow.blur)];
    
    this.shadowLayer.addChild(shadowGraphics);
  }

  /**
   * Draw all segments with theme-based styling
   */
  private drawSegments(): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    let currentAngle = -Math.PI / 2; // Start at top

    segments.forEach((segment, index) => {
      const segmentAngle = (segment.weight / totalWeight) * Math.PI * 2;
      this.drawSegment(segment, currentAngle, segmentAngle, index);
      currentAngle += segmentAngle;
    });
  }

  private drawSegment(
    segment: WheelSegment,
    startAngle: number,
    angle: number,
    _index: number
  ): void {
    const graphics = new PIXI.Graphics();
    const baseColor = parseColor(segment.color);
    
    // Apply gradient if enabled
    if (this.theme.wheel.segments.gradient.enabled) {
      // Create gradient effect (simple: draw multiple overlays)
      const { lift, shade } = this.theme.wheel.segments.gradient;
      
      // Base segment
      graphics.beginFill(baseColor, 1);
      graphics.moveTo(0, 0);
      graphics.arc(0, 0, this.wheelRadius, startAngle, startAngle + angle);
      graphics.lineTo(0, 0);
      graphics.endFill();

      // Highlight overlay (top half)
      graphics.beginFill(0xFFFFFF, lift);
      graphics.moveTo(0, 0);
      graphics.arc(0, 0, this.wheelRadius * 0.5, startAngle, startAngle + angle);
      graphics.lineTo(0, 0);
      graphics.endFill();

      // Shade overlay (bottom edge)
      graphics.beginFill(0x000000, shade);
      graphics.moveTo(
        Math.cos(startAngle) * this.wheelRadius * 0.8,
        Math.sin(startAngle) * this.wheelRadius * 0.8
      );
      graphics.arc(0, 0, this.wheelRadius, startAngle, startAngle + angle);
      graphics.arc(0, 0, this.wheelRadius * 0.8, startAngle + angle, startAngle, true);
      graphics.closePath();
      graphics.endFill();
    } else {
      // Flat segment
      graphics.beginFill(baseColor, 1);
      graphics.moveTo(0, 0);
      graphics.arc(0, 0, this.wheelRadius, startAngle, startAngle + angle);
      graphics.lineTo(0, 0);
      graphics.endFill();
    }

    // Bevel effect from theme
    if (this.theme.wheel.bevel.enabled) {
      const { highlight, shade, strength } = this.theme.wheel.bevel;
      
      // Light edge (highlight)
      graphics.lineStyle(2 * strength, parseColor(highlight.color), highlight.alpha);
      graphics.moveTo(0, 0);
      graphics.lineTo(
        Math.cos(startAngle) * this.wheelRadius,
        Math.sin(startAngle) * this.wheelRadius
      );

      // Dark edge (shade)
      graphics.lineStyle(2 * strength, parseColor(shade.color), shade.alpha);
      graphics.moveTo(0, 0);
      graphics.lineTo(
        Math.cos(startAngle + angle) * this.wheelRadius,
        Math.sin(startAngle + angle) * this.wheelRadius
      );
    }

    // Segment stroke from theme
    const { stroke } = this.theme.wheel.segments;
    graphics.lineStyle(stroke.width, parseColor(stroke.color), stroke.alpha);
    graphics.moveTo(0, 0);
    graphics.lineTo(
      Math.cos(startAngle) * this.wheelRadius,
      Math.sin(startAngle) * this.wheelRadius
    );

    this.wheel.addChild(graphics);

    // Add text label with theme styling
    this.drawSegmentText(segment.label, segment.color, startAngle + angle / 2, angle);
  }

  private drawSegmentText(
    label: string,
    segmentColor: string,
    midAngle: number,
    segmentAngle: number
  ): void {
    const textRadius = this.wheelRadius * 0.7;
    const textTheme = this.theme.wheel.text;
    
    // Auto-contrast or use theme default
    const textColor = getContrastColor(segmentColor);

    // Auto font sizing
    const baseSize = Math.max(14, this.wheelRadius / 12);
    const sizeMultiplier = Math.min(1, segmentAngle / (Math.PI / 4));
    const lengthMultiplier = Math.min(1, 15 / label.length);
    const fontSize = baseSize * sizeMultiplier * lengthMultiplier;

    // Truncate if too long
    const maxLength = 20;
    const displayLabel = label.length > maxLength ? label.substring(0, maxLength - 1) + '…' : label;

    const textStyle: Partial<PIXI.TextStyle> = {
      fontFamily: textTheme.fontFamily,
      fontSize,
      fontWeight: String(textTheme.fontWeight) as any,
      fill: textColor,
      align: 'center',
    };

    // Add stroke if theme has it
    if (textTheme.stroke) {
      textStyle.stroke = {
        color: textTheme.stroke.color,
        width: textTheme.stroke.width
      };
    }

    // Add shadow from theme
    textStyle.dropShadow = {
      color: textTheme.shadow.color,
      alpha: textTheme.shadow.alpha,
      blur: textTheme.shadow.blur,
      angle: Math.PI / 4,
      distance: textTheme.shadow.y
    };

    const text = new PIXI.Text(displayLabel, textStyle);

    text.anchor.set(0.5);
    text.x = Math.cos(midAngle) * textRadius;
    text.y = Math.sin(midAngle) * textRadius;
    text.rotation = midAngle + Math.PI / 2;

    this.wheel.addChild(text);
  }

  /**
   * Draw inner rim (darker, for depth)
   */
  private drawInnerRim(): void {
    const { rim } = this.theme.wheel;
    const innerRim = new PIXI.Graphics();
    
    // Inner rim with gradient (simulate with multiple colors)
    const [_color1, color2, _color3] = rim.innerGradient;
    
    innerRim.lineStyle(6, parseColor(color2), 0.3);
    innerRim.drawCircle(0, 0, this.wheelRadius - rim.width / 2);
    
    this.wheel.addChild(innerRim);
  }

  /**
   * Draw outer rim (from theme gradient)
   */
  private drawOuterRim(): void {
    const { rim } = this.theme.wheel;
    const outerRim = new PIXI.Graphics();
    
    // Outer border with gradient
    const [color1, color2, _color3] = rim.outerGradient;
    
    // Main rim
    outerRim.lineStyle(rim.width, parseColor(color2), 1);
    outerRim.drawCircle(0, 0, this.wheelRadius);
    
    // Inner highlight
    outerRim.lineStyle(2, parseColor(color1), 0.5);
    outerRim.drawCircle(0, 0, this.wheelRadius - rim.width / 2);
    
    this.wheel.addChild(outerRim);
  }

  /**
   * Draw gloss overlay (shine effect from theme)
   */
  private drawGloss(): void {
    const { gloss } = this.theme.wheel;
    const glossGraphics = new PIXI.Graphics();
    
    // Arc of light
    const angleRad = (gloss.angleDeg * Math.PI) / 180;
    const arcLength = Math.PI / 3; // 60 degrees
    
    glossGraphics.lineStyle(3, 0xFFFFFF, gloss.alpha);
    glossGraphics.arc(0, 0, this.wheelRadius * 0.95, angleRad, angleRad + arcLength);
    
    glossGraphics.filters = [new PIXI.BlurFilter(4)];
    
    this.wheel.addChild(glossGraphics);
  }

  /**
   * Draw center circle with theme-based styling
   */
  private drawCenterCircle(): void {
    const { center } = this.theme.wheel;
    const centerGraphics = new PIXI.Graphics();
    
    // Cap with gradient
    const [_capColor1, capColor2, _capColor3] = center.cap.gradient;
    centerGraphics.beginFill(parseColor(capColor2), 1);
    
    // Ring
    const [_ringColor1, ringColor2, _ringColor3] = center.ring.gradient;
    centerGraphics.lineStyle(center.ring.width, parseColor(ringColor2), 1);
    
    centerGraphics.drawCircle(0, 0, 40);
    centerGraphics.endFill();

    // Glass highlight (top-left shine)
    centerGraphics.beginFill(0xFFFFFF, 0.2);
    centerGraphics.drawCircle(-5, -5, 25);
    centerGraphics.endFill();

    // Add CTA text (from config, not theme)
    const spinText = new PIXI.Text(this.config.ctaText || 'SPIN', {
      fontFamily: this.theme.wheel.text.fontFamily,
      fontSize: 14,
      fontWeight: '700',
      fill: this.theme.wheel.text.fill,
      align: 'center'
    });
    spinText.anchor.set(0.5);
    centerGraphics.addChild(spinText);

    this.wheel.addChild(centerGraphics);
  }

  public getWheel(): PIXI.Container {
    return this.wheel;
  }

  public updateConfig(config: WheelConfig): void {
    this.config = config;
    const themeKey = (config.theme as keyof typeof WHEEL_THEMES) || 'modern';
    this.theme = WHEEL_THEMES[themeKey] || WHEEL_THEMES.modern;
    
    // Force complete re-render
    this.shadowLayer.removeChildren();
    this.wheel.removeChildren();
    this.render();
  }

  /**
   * Highlight winning segment (called on win)
   */
  public highlightWinningSegment(segmentIndex: number): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    let currentAngle = -Math.PI / 2;
    
    for (let i = 0; i < segments.length; i++) {
      const segmentAngle = (segments[i].weight / totalWeight) * Math.PI * 2;
      
      if (i === segmentIndex) {
        const midAngle = currentAngle + segmentAngle / 2;
        this.drawWinGlow(midAngle, segmentAngle);
        break;
      }
      
      currentAngle += segmentAngle;
    }
  }

  /**
   * Draw glow ring around winning segment
   */
  private drawWinGlow(midAngle: number, segmentAngle: number): void {
    const glow = new PIXI.Graphics();
    
    glow.lineStyle(8, 0xffdd00, 0.8);
    glow.arc(
      0,
      0,
      this.wheelRadius + 5,
      midAngle - segmentAngle / 2,
      midAngle + segmentAngle / 2
    );
    
    glow.filters = [new PIXI.BlurFilter(12)];
    
    this.wheel.addChild(glow);
    
    // Remove glow after 2 seconds
    setTimeout(() => {
      this.wheel.removeChild(glow);
    }, 2000);
  }
}
